import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { ArrowUp } from '@/app/components/icons/ArrowUp';
import { CLAW_RULES_URL, SITE } from '@/app/constants';
import { reportEvent } from '@/app/utils';
import { openInNewTab } from '@/app/utils/client';
import { useLocale } from '@/i18n/routing';
import { PRICING_FAQ } from '../constants';
import type { FaqItem } from '../types';

function FaqAccordionItem({ item }: { item: FaqItem }) {
  const { id, question, children } = item;
  return (
    <details className="group">
      <summary
        className="flex cursor-pointer items-center justify-between rounded-16 bg-[rgba(27,35,43,0.60)] px-6 py-5 font-semibold text-base text-white focus:outline-none sm:rounded-lg sm:px-4 sm:py-4"
        onClick={() => {
          reportEvent('element_click', {
            from_page: 'my-subscription',
            element_name: `${id}`,
          });
          if (id === 'question_clawPass') {
            openInNewTab(CLAW_RULES_URL);
          }
        }}
      >
        <div className="flex items-center">{question}</div>
        {children.length > 0 && (
          <ArrowUp className="size-4 transform transition-transform duration-200 group-open:rotate-180 sm:h-5 sm:w-5" />
        )}
      </summary>
      {children.length > 0 && (
        <div className="mb-6 flex flex-col items-start gap-10 self-stretch py-4 pr-6 pl-6 text-sm">
          {children.map((item) => (
            <div
              key={item.question}
              className="flex flex-col items-start justify-start gap-2 self-stretch pl-8 text-white sm:pl-0"
            >
              <div className="justify-start font-medium leading-7 md:text-sm">{item.question}</div>
              <p className="whitespace-pre-line opacity-60 sm:pl-0 sm:text-sm">{item.answer}</p>
            </div>
          ))}
        </div>
      )}
    </details>
  );
}

export const FAQ: FC = () => {
  const t = useTranslations();
  const locale = useLocale();
  const faqData = PRICING_FAQ[SITE][locale];
  return (
    <section className="pt-10 sm:pt-8">
      <div className="mx-auto w-full max-w-[1192px] px-6 sm:px-4">
        <h2 className="mb-8 text-center font-semibold text-white text-xl sm:mb-8">
          {t('payments.mySubscription.faqTitle')}
        </h2>
        <div className="mx-auto flex w-full flex-col gap-4">
          {faqData?.map((faq) => (
            <div key={faq.id} className="overflow-hidden">
              <FaqAccordionItem item={faq} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
